/**
 * @author mschuetz / http://mschuetz.at
 *
 *
 */

import * as THREE from "../../libs/three.js/build/three.module.js";
import { KeyCodes } from "../KeyCodes.js";
import { Utils } from "../utils.js";
import { EventDispatcher } from "../EventDispatcher.js";
import { YDBoxVolume } from "../utils/Volume.js";

export class InputHandler extends EventDispatcher {
	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.renderer = viewer.renderer;
		this.domElement = this.renderer.domElement;
		this.enabled = true;

		this.scene = null;
		this.interactiveScenes = [];
		this.interactiveObjects = new Set();
		this.inputListeners = [];
		this.blacklist = new Set();

		this.drag = null;
		this.mouse = new THREE.Vector2(0, 0);

		this.selection = [];

		this.hoveredElements = [];
		this.pressedKeys = {};

		this.wheelDelta = 0;

		this.speed = 1;

		this.logMessages = false;

		if (this.domElement.tabIndex === -1) {
			this.domElement.tabIndex = 2222;
		}

		this.domElement.addEventListener('contextmenu', (event) => { event.preventDefault(); }, false);
		this.domElement.addEventListener('click', this.onMouseClick.bind(this), false);
		this.domElement.addEventListener('mousedown', this.onMouseDown.bind(this), false);
		this.domElement.addEventListener('mouseup', this.onMouseUp.bind(this), false);
		this.domElement.addEventListener('mousemove', this.onMouseMove.bind(this), false);
		this.domElement.addEventListener('mousewheel', this.onMouseWheel.bind(this), false);
		this.domElement.addEventListener('DOMMouseScroll', this.onMouseWheel.bind(this), false); // Firefox
		this.domElement.addEventListener('dblclick', this.onDoubleClick.bind(this));
		this.domElement.addEventListener('keydown', this.onKeyDown.bind(this));
		this.domElement.addEventListener('keyup', this.onKeyUp.bind(this));
		this.domElement.addEventListener('touchstart', this.onTouchStart.bind(this));
		this.domElement.addEventListener('touchend', this.onTouchEnd.bind(this));
		this.domElement.addEventListener('touchmove', this.onTouchMove.bind(this));
	}

	addInputListener(listener) {
		this.inputListeners.push(listener);
	}

	removeInputListener(listener) {
		this.inputListeners = this.inputListeners.filter(e => e !== listener);
	}

	getSortedListeners() {
		return this.inputListeners.sort((a, b) => {
			let ia = (a.importance !== undefined) ? a.importance : 0;
			let ib = (b.importance !== undefined) ? b.importance : 0;

			return ib - ia;
		});
	}

	onTouchStart(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onTouchStart');

		e.preventDefault();

		if (e.touches.length === 1) {
			let rect = this.domElement.getBoundingClientRect();
			let x = e.touches[0].pageX - rect.left;
			let y = e.touches[0].pageY - rect.top;
			this.mouse.set(x, y);

			this.startDragging(null);
		}


		for (let inputListener of this.getSortedListeners()) {
			inputListener.dispatchEvent({
				type: e.type,
				touches: e.touches,
				changedTouches: e.changedTouches
			});
		}
	}

	onTouchEnd(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onTouchEnd');

		e.preventDefault();

		for (let inputListener of this.getSortedListeners()) {
			inputListener.dispatchEvent({
				type: 'drop',
				drag: this.drag,
				viewer: this.viewer
			});
		}

		this.drag = null;

		for (let inputListener of this.getSortedListeners()) {
			inputListener.dispatchEvent({
				type: e.type,
				touches: e.touches,
				changedTouches: e.changedTouches
			});
		}
	}

	onTouchMove(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onTouchMove');

		e.preventDefault();

		if (e.touches.length === 1) {
			let rect = this.domElement.getBoundingClientRect();
			let x = e.touches[0].pageX - rect.left;
			let y = e.touches[0].pageY - rect.top;
			this.mouse.set(x, y);

			if (this.drag) {
				this.drag.mouse = 1;

				this.drag.lastDrag.x = x - this.drag.end.x;
				this.drag.lastDrag.y = y - this.drag.end.y;

				this.drag.end.set(x, y);

				if (this.logMessages) console.log(this.constructor.name + ': drag: ');
				for (let inputListener of this.getSortedListeners()) {
					inputListener.dispatchEvent({
						type: 'drag',
						drag: this.drag,
						viewer: this.viewer
					});
				}
			}
		}

		for (let inputListener of this.getSortedListeners()) {
			inputListener.dispatchEvent({
				type: e.type,
				touches: e.touches,
				changedTouches: e.changedTouches
			});
		}

		// DEBUG CODE
		// let debugTouches = [...e.touches, {
		//	pageX: this.domElement.clientWidth / 2,
		//	pageY: this.domElement.clientHeight / 2}];
		// for(let inputListener of this.getSortedListeners()){
		//	inputListener.dispatchEvent({
		//		type: e.type,
		//		touches: debugTouches,
		//		changedTouches: e.changedTouches
		//	});
		// }
	}

	onKeyDown(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onKeyDown');

		// DELETE
		if (e.keyCode === KeyCodes.DELETE && this.selection.length > 0) {
			this.dispatchEvent({
				type: 'delete',
				selection: this.selection
			});

			this.deselectAll();
		}

		this.dispatchEvent({
			type: 'keydown',
			keyCode: e.keyCode,
			event: e
		});

		// for(let l of this.getSortedListeners()){
		//	l.dispatchEvent({
		//		type: "keydown",
		//		keyCode: e.keyCode,
		//		event: e
		//	});
		// }

		this.pressedKeys[e.keyCode] = true;

		// e.preventDefault();
	}

	onKeyUp(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onKeyUp');

		delete this.pressedKeys[e.keyCode];

		e.preventDefault();
	}

	onDoubleClick(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onDoubleClick');

		let consumed = false;
		for (let hovered of this.hoveredElements) {
			if (hovered._listeners && hovered._listeners['dblclick']) {
				hovered.object.dispatchEvent({
					type: 'dblclick',
					mouse: this.mouse,
					object: hovered.object
				});
				consumed = true;
				break;
			}
		}

		if (!consumed) {
			for (let inputListener of this.getSortedListeners()) {
				inputListener.dispatchEvent({
					type: 'dblclick',
					mouse: this.mouse,
					object: null
				});
			}
		}

		e.preventDefault();
	}

	onMouseClick(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onMouseClick');

		// 优先处理 YDBoxVolume 的交互按钮（PPT 翻页 / 视频·音频播放 / PDF 翻页）。
		// 按钮不在选中拾取体系内，这里用点击时刻的射线单独命中，命中即触发按钮动作并跳过后续派发，
		// 避免走到包围盒选中逻辑（选中会弹出 transform gizmo 遮挡按钮）。
		let hoveredBtn = this.getHoveredClickButton();
		if (hoveredBtn) {
			const { volume, btn, uv } = hoveredBtn;
			if (typeof btn.onClickAt === 'function') {
				// 胶囊控制条：按命中 uv 拆分点击区域（PPT 上一页/下一页）
				btn.onClickAt(uv);
			} else if (typeof btn.onClick === 'function') {
				btn.onClick();
			} else {
				// 兼容视频/PDF/音频旧逻辑：其 click 分支依赖 this.clickBtn.highlight
				btn.highlight = true;
				volume.dispatchEvent({ type: 'click' });
			}
			e.preventDefault();
			return;
		}

		let selectable = this.hoveredElements
			.find(el => el.object._listeners && el.object._listeners['select']);
		if (selectable && selectable.object) {
			// 全景观看态且未命中视频球：不派发 click → 不进入沉浸。
			// 这次点击留给 onMouseUp 的选中逻辑去选中包围盒（弹 transform gizmo）。
			if (!this.isPanoBoxOuterPick(selectable.object)) {
				selectable.object.dispatchEvent({
					type: 'click',
				});
			}
		}

		e.preventDefault();
	}

	onMouseDown(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onMouseDown');

		e.preventDefault();

		let consumed = false;
		let consume = () => { return consumed = true; };
		if (this.hoveredElements.length === 0) {
			for (let inputListener of this.getSortedListeners()) {
				inputListener.dispatchEvent({
					type: 'mousedown',
					viewer: this.viewer,
					mouse: this.mouse
				});
			}
		} else {
			for (let hovered of this.hoveredElements) {
				let object = hovered.object;
				object.dispatchEvent({
					type: 'mousedown',
					viewer: this.viewer,
					consume: consume
				});

				if (consumed) {
					break;
				}
			}
		}

		if (!this.drag) {
			let target = this.hoveredElements
				.find(el => (
					el.object._listeners &&
					el.object._listeners['drag'] &&
					el.object._listeners['drag'].length > 0));
			let targetYdBox = this.hoveredElements
				.find(el => (
					el.object._listeners &&
					el.object._listeners['deselect'] &&
					el.object._listeners['deselect'].length > 0));
			if (target && targetYdBox) {
				this.startDraggingWithLock(target.object, targetYdBox.object, { location: target.point });
			} else if (target) {
				this.startDragging(target.object, { location: target.point });
			} else {
				this.startDragging(null);
			}
		}

		if (this.scene) {
			this.viewStart = this.scene.view.clone();
		}
	}

	onMouseUp(e) {
		if (this.logMessages) console.log(this.constructor.name + ': onMouseUp');

		e.preventDefault();

		let noMovement = this.getNormalizedDrag().length() === 0;


		let consumed = false;
		let consume = () => { return consumed = true; };
		if (this.hoveredElements.length === 0) {
			for (let inputListener of this.getSortedListeners()) {
				inputListener.dispatchEvent({
					type: 'mouseup',
					viewer: this.viewer,
					mouse: this.mouse,
					consume: consume
				});

				if (consumed) {
					break;
				}
			}
		} else {
			let selectable = this.hoveredElements
				.find(e => e.object && e.object._listeners && e.object._listeners['mouseup']);
			if (selectable) {
				const object = selectable.object
				object.dispatchEvent({
					type: 'mouseup',
					viewer: this.viewer,
					faceIndex: selectable ? selectable.faceIndex : null,
					consume: consume
				});
			}
		}

		if (this.drag) {
			if (this.drag.object) {
				if (this.logMessages) console.log(`${this.constructor.name}: drop ${this.drag.object.name}`);
				this.drag.object.dispatchEvent({
					type: 'drop',
					drag: this.drag,
					viewer: this.viewer

				});
			} else {
				for (let inputListener of this.getSortedListeners()) {
					inputListener.dispatchEvent({
						type: 'drop',
						drag: this.drag,
						viewer: this.viewer
					});
				}
			}

			// check for a click
			let clicked = this.hoveredElements.map(h => h.object).find(v => v === this.drag.object) !== undefined;
			if (clicked) {
				if (this.logMessages) console.log(`${this.constructor.name}: click ${this.drag.object.name}`);
				this.drag.object.dispatchEvent({
					type: 'click',
					viewer: this.viewer,
					consume: consume,
				});
			}

			this.drag = null;
		}

		if (!consumed) {
			if (e.button === THREE.MOUSE.LEFT) {
				if (noMovement) {
					// 点击落在 YDBoxVolume 的按钮上时，不选中包围盒（否则弹出的 transform gizmo 会遮挡按钮，
					// 使后续点击落到 gizmo 上而无法翻页/播放）。按钮动作由 onMouseClick 触发。
					if (this.getHoveredClickButton()) {
						// 命中按钮：跳过选中逻辑
					} else if (this.getHoveredTransformHandle()) {
						// 点击落在 transform gizmo 手柄上：不切换选中。
						// 嵌套盒场景下手柄与外层盒同射线，放行走选中分支会把手柄点击
						// 劫持成选中外层盒（selectable 取射线首命中者），gizmo 随之换绑目标
					} else {
						let selectable = this.hoveredElements
							.find(el => el.object._listeners && el.object._listeners['select']);
						// let selectable = null;
						// for (let i = this.hoveredElements.length - 1; i >= 0; i--) {
						// 	if (this.hoveredElements[i].object._listeners && this.hoveredElements[i].object._listeners['select']) {
						// 		selectable = this.hoveredElements[i];
						// 		break;
						// 	}
						// }
						if (selectable && this.isPanoSpherePick(selectable.object)) {
							// 全景观看态 + 点击穿过了视频球：这次点击去进入沉浸，不选中盒子。
							// 若在此选中，退出沉浸后 transform gizmo 会残留弹出。
							// 未命中球体（但命中盒子）的点击不走这里，落到下面正常选中 → 弹 gizmo 可编辑。
							// 与上面「命中按钮」一致，不改动已有选中状态（不做 deselectAll）。
						} else if (selectable) {
							let object = selectable.object;

							if (this.isSelected(object)) {
								this.selection
									.filter(e => e !== object)
									.forEach(e => this.toggleSelection(e, selectable));
							} else {
								this.deselectAll();
								this.toggleSelection(object, selectable);
							}
						} else {
							this.deselectAll();
						}
					}
				}
			} else if ((e.button === THREE.MOUSE.RIGHT) && noMovement) {
				this.deselectAll();
			}
		}
	}

	onMouseMove(e) {
		e.preventDefault();
		// return;
		let rect = this.domElement.getBoundingClientRect();
		let x = e.clientX - rect.left;
		let y = e.clientY - rect.top;
		this.mouse.set(x, y);

		let hoveredElements = this.getHoveredElements();
		if (hoveredElements.length > 0) {
			let names = hoveredElements.map(h => h.object.name).join(", ");
			if (this.logMessages) console.log(`${this.constructor.name}: onMouseMove; hovered: '${names}'`);
		}

		if (this.drag) {
			this.drag.mouse = e.buttons;

			this.drag.lastDrag.x = x - this.drag.end.x;
			this.drag.lastDrag.y = y - this.drag.end.y;

			this.drag.end.set(x, y);

			if (this.drag.object) {
				if (this.logMessages) console.log(this.constructor.name + ': drag: ' + this.drag.object.name);
				this.drag.object.dispatchEvent({
					type: 'drag',
					drag: this.drag,
					viewer: this.viewer
				});
			} else {
				if (this.logMessages) console.log(this.constructor.name + ': drag: ');

				let dragConsumed = false;
				for (let inputListener of this.getSortedListeners()) {
					inputListener.dispatchEvent({
						type: 'drag',
						drag: this.drag,
						viewer: this.viewer,
						consume: () => { dragConsumed = true; }
					});

					if (dragConsumed) {
						break;
					}
				}
			}
		} else {
			let curr = hoveredElements.map(a => a.object).find(a => true);
			let prev = this.hoveredElements.map(a => a.object).find(a => true);

			if (curr !== prev) {
				if (curr) {
					if (this.logMessages) console.log(`${this.constructor.name}: mouseover: ${curr.name}`);
					curr.dispatchEvent({
						type: 'mouseover',
						object: curr,
					});
				}
				if (prev) {
					if (this.logMessages) console.log(`${this.constructor.name}: mouseleave: ${prev.name}`);
					prev.dispatchEvent({
						type: 'mouseleave',
						object: prev,
					});
				}
			}

			if (hoveredElements.length > 0) {
				let object = hoveredElements
					.map(e => e.object)
					.find(e => (e._listeners && e._listeners['mousemove']));

				if (object) {
					object.dispatchEvent({
						type: 'mousemove',
						object: object
					});
				}
			}

		}

		// for (let inputListener of this.getSortedListeners()) {
		// 	inputListener.dispatchEvent({
		// 		type: 'mousemove',
		// 		object: null
		// 	});
		// }


		this.hoveredElements = hoveredElements;
	}

	onMouseWheel(e) {
		if (!this.enabled) return;

		if (this.logMessages) console.log(this.constructor.name + ": onMouseWheel");

		e.preventDefault();

		let delta = 0;
		if (e.wheelDelta !== undefined) { // WebKit / Opera / Explorer 9
			delta = e.wheelDelta;
		} else if (e.detail !== undefined) { // Firefox
			delta = -e.detail;
		}

		let ndelta = Math.sign(delta);

		// this.wheelDelta += Math.sign(delta);

		if (this.hoveredElement) {
			this.hoveredElement.object.dispatchEvent({
				type: 'mousewheel',
				delta: ndelta,
				object: this.hoveredElement.object
			});
		} else {
			for (let inputListener of this.getSortedListeners()) {
				inputListener.dispatchEvent({
					type: 'mousewheel',
					delta: ndelta,
					object: null
				});
			}
		}
	}

	startDraggingWithLock(object, ydBoxObject, args = null) {

		let name = object ? object.name : "no name";
		if (this.logMessages) console.log(`${this.constructor.name}: startDragging: '${name}'`);

		this.drag = {
			start: this.mouse.clone(),
			end: this.mouse.clone(),
			lastDrag: new THREE.Vector2(0, 0),
			startView: this.scene.view.clone(),
			object: object,
			ydObject: ydBoxObject
		};

		if (args) {
			for (let key of Object.keys(args)) {
				this.drag[key] = args[key];
			}
		}
	}
	startDragging(object, args = null) {

		let name = object ? object.name : "no name";
		if (this.logMessages) console.log(`${this.constructor.name}: startDragging: '${name}'`);

		this.drag = {
			start: this.mouse.clone(),
			end: this.mouse.clone(),
			lastDrag: new THREE.Vector2(0, 0),
			startView: this.scene.view.clone(),
			object: object
		};

		if (args) {
			for (let key of Object.keys(args)) {
				this.drag[key] = args[key];
			}
		}
	}

	getMousePointCloudIntersection(mouse) {
		return Utils.getMousePointCloudIntersection(
			this.mouse,
			this.scene.getActiveCamera(),
			this.viewer,
			this.scene.pointclouds);
	}

	setSelection(selection) {
		// 整体替换选中集（与 toggleSelection 的单个增删相对）。本 fork 此前没有
		// 这个方法，而多边形棱柱的面板导入/页面导入/采点闭合三处都在调它——
		// 缺失时调用点抛 TypeError 被各自 catch 吞掉，选中链路静默失效。
		let oldSelection = this.selection;

		for (const object of oldSelection) {
			object.dispatchEvent({ type: 'deselect' });
		}
		this.selection = selection;
		for (const object of selection) {
			object.dispatchEvent({ type: 'select' });
		}

		this.dispatchEvent({
			type: 'selection_changed',
			oldSelection: oldSelection,
			selection: this.selection
		});
	}

	toggleSelection(object, selectable) {
		let oldSelection = this.selection;

		let index = this.selection.indexOf(object);

		if (index === -1) {
			this.selection.push(object);
			object.dispatchEvent({
				type: 'select',
				faceIndex: selectable ? selectable.faceIndex : null
			});
		} else {
			this.selection.splice(index, 1);
			object.dispatchEvent({
				type: 'deselect',
				faceIndex: selectable ? selectable.faceIndex : null
			});
		}

		this.dispatchEvent({
			type: 'selection_changed',
			oldSelection: oldSelection,
			selection: this.selection
		});
	}

	deselect(object) {

		let oldSelection = this.selection;

		let index = this.selection.indexOf(object);

		if (index >= 0) {
			this.selection.splice(index, 1);
			object.dispatchEvent({
				type: 'deselect'
			});
			this.dispatchEvent({
				type: 'selection_changed',
				oldSelection: oldSelection,
				selection: this.selection
			});
		}
	}

	deselectAll() {
		for (let object of this.selection) {
			object.dispatchEvent({
				type: 'deselect'
			});
		}

		let oldSelection = this.selection;

		if (this.selection.length > 0) {
			this.selection = [];
			this.dispatchEvent({
				type: 'selection_changed',
				oldSelection: oldSelection,
				selection: this.selection
			});
		}
	}

	isSelected(object) {
		let index = this.selection.indexOf(object);

		return index !== -1;
	}

	registerInteractiveObject(object) {
		this.interactiveObjects.add(object);
	}

	removeInteractiveObject(object) {
		this.interactiveObjects.delete(object);
	}

	registerInteractiveScene(scene) {
		let index = this.interactiveScenes.indexOf(scene);
		if (index === -1) {
			this.interactiveScenes.push(scene);
		}
	}

	unregisterInteractiveScene(scene) {
		let index = this.interactiveScenes.indexOf(scene);
		if (index > -1) {
			this.interactiveScenes.splice(index, 1);
		}
	}

	getHoveredElement() {
		let hoveredElements = this.getHoveredElements();
		if (hoveredElements.length > 0) {
			return hoveredElements[0];
		} else {
			return null;
		}
	}

	getHoveredElements() {
		let scenes = this.interactiveScenes.concat(this.scene.scene);

		let interactableListeners = ['mouseup', 'mousemove', 'mouseover', 'mouseleave', 'drag', 'drop', 'click', 'select', 'deselect'];
		let interactables = [];
		for (let scene of scenes) {
			scene.traverseVisible(node => {
				// if (node._listeners && node.visible && !this.blacklist.has(node)) {
				if (node._listeners && node.visible) {
					let hasInteractableListener = interactableListeners.filter((e) => {
						return node._listeners[e] !== undefined;
					}).length > 0;

					if (hasInteractableListener) {
						interactables.push(node);
					}
				}
			});
		}

		let camera = this.scene.getActiveCamera();
		let ray = Utils.mouseToRay(this.mouse, camera, this.domElement.clientWidth, this.domElement.clientHeight);

		let raycaster = new THREE.Raycaster();
		raycaster.ray.set(ray.origin, ray.direction);
		raycaster.params.Line.threshold = 0.2;

		let intersections = raycaster.intersectObjects(interactables.filter(o => o.visible || o instanceof YDBoxVolume), false);

		return intersections;
	}

	// 点击时刻单独命中 YDBoxVolume 的交互按钮（PPT 上一页/下一页、视频/音频播放、PDF 翻页）。
	// 这些按钮是 volume 的子 mesh，不在 getHoveredElements 的选中拾取体系内（那里只测 box），
	// 因此这里用当前鼠标射线直接对所有 clickBtns 做一次射线检测，命中返回 { volume, btn }，否则 null。
	getHoveredClickButton() {
		if (!this.scene || !this.scene.volumes) return null;
		let volumes = this.scene.volumes.filter(v =>
			v instanceof YDBoxVolume && v._visible && v.clickBtns && v.clickBtns.length);
		if (volumes.length === 0) return null;

		let btns = [];
		let owner = new Map();
		for (const v of volumes) {
			for (const b of v.clickBtns) {
				// 隐藏的控制条（PPT 未悬停时）不可点击；射线检测不理会 visible，需显式排除
				if (b.visible === false) continue;
				btns.push(b);
				owner.set(b, v);
			}
		}
		if (btns.length === 0) return null;

		let camera = this.scene.getActiveCamera();
		let ray = Utils.mouseToRay(this.mouse, camera, this.domElement.clientWidth, this.domElement.clientHeight);
		let raycaster = new THREE.Raycaster(ray.origin, ray.direction);
		let intersects = raycaster.intersectObjects(btns, true);
		if (intersects.length > 0) {
			let btn = intersects[0].object;
			// uv 用于把「胶囊控制条」按命中位置拆分为 上一页/页码/下一页 三段（PPT）
			return { volume: owner.get(btn), btn, uv: intersects[0].uv };
		}
		return null;
	}

	// 射线下是否命中 transform gizmo 的手柄拾取体（pickVolume/pickSphere 均带 .handle 标记，
	// 全 src 仅 TransformationTool 赋值该属性）。命中时不触发选中切换，防嵌套盒场景劫持。
	getHoveredTransformHandle() {
		return this.hoveredElements.find(el => el.object.handle !== undefined) || null;
	}

	// 全景视频盒的「观看态」（id=10 且允许点击进入沉浸）—— 辅助判定合集。
	// 命中的始终是盒 mesh（球 raycast 被禁用），用点击时刻射线补算球体相交。
	// 三种情形：
	//   isPanoViewingPick    → 是否处于观看态（id=10 + panoEnterEnabled）
	//   isPanoSpherePick     → 射线穿过全景球 → 应进入沉浸
	//   isPanoBoxOuterPick   → 未命中球但命中盒 → 走普通选中（弹 gizmo）
	isPanoViewingPick(object) {
		if (!(object instanceof YDBoxVolume)) return false;
		if (typeof object.isPanoViewingMode !== 'function') return false;
		return object.isPanoViewingMode();
	}

	_panoRayFromClick() {
		if (!this.scene) return null;
		let camera = this.scene.getActiveCamera();
		return Utils.mouseToRay(this.mouse, camera, this.domElement.clientWidth, this.domElement.clientHeight);
	}

	isPanoSpherePick(object) {
		if (!this.isPanoViewingPick(object)) return false;
		let ray = this._panoRayFromClick();
		return !!ray && object.isPanoSphereHit(ray);
	}

	isPanoBoxOuterPick(object) {
		if (!this.isPanoViewingPick(object)) return false;
		let ray = this._panoRayFromClick();
		return !!ray && !object.isPanoSphereHit(ray);
	}

	setScene(scene) {
		this.deselectAll();

		this.scene = scene;
	}

	update(delta) {

	}

	getNormalizedDrag() {
		if (!this.drag) {
			return new THREE.Vector2(0, 0);
		}

		let diff = new THREE.Vector2().subVectors(this.drag.end, this.drag.start);

		diff.x = diff.x / this.domElement.clientWidth;
		diff.y = diff.y / this.domElement.clientHeight;

		return diff;
	}

	getNormalizedLastDrag() {
		if (!this.drag) {
			return new THREE.Vector2(0, 0);
		}

		let lastDrag = this.drag.lastDrag.clone();

		lastDrag.x = lastDrag.x / this.domElement.clientWidth;
		lastDrag.y = lastDrag.y / this.domElement.clientHeight;

		return lastDrag;
	}
}
